import React, { useRef, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { login } from '../../../lib/authentication';
import { RESCUE_LOGIN_PENDING_TRACK_KEY, trackLoginFailed } from '../../analytics';
import useTrackRescuePageView from '../../hooks/useTrackRescuePageView';
import { AppState } from '../../../lib/store';
import { useQueryParams } from '../../../lib/useQueryParams';
import fiLogo from '../../assets/fi-logo-black.svg';
import eyeVisibleIcon from '../../../assets/images/eye-visible.svg';
import eyeHiddenIcon from '../../../assets/images/eye-hidden.svg';
import styles from './RescueLogin.module.scss';

interface RescueLoginQueryParams {
  redirectUri?: string;
}

export default function RescueLogin() {
  const { redirectUri } = useQueryParams<RescueLoginQueryParams>();
  const isLoggedIn = useSelector((state: AppState) => !!state.session);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shaking, setShaking] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // This route is mounted outside RescuePortal, so the router-level pageview
  // tracker in RescuePortal.tsx never fires here. Skip it when already logged
  // in, since we immediately redirect away below.
  useTrackRescuePageView('/rescue/login', 'Login', !isLoggedIn);

  if (isLoggedIn) {
    return <Redirect to={redirectUri || '/rescue'} />;
  }

  document.title = 'Log In — Fi for Rescues';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    setEmailError(false);
    setPasswordError(false);
    setFormError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      if (!trimmedEmail) {
        setEmailError(true);
      }
      if (!password) {
        setPasswordError(true);
      }
      return;
    }

    setSubmitting(true);

    try {
      await login(trimmedEmail, password);
      // Defer the analytics event until RescueContextProvider has loaded org
      // context, so the event ships with rescue_org_id / rescue_org_name set.
      sessionStorage.setItem(RESCUE_LOGIN_PENDING_TRACK_KEY, '1');
    } catch (err) {
      const message = (err as Error).message;
      const reason = /invalid email or password/i.test(message) ? 'invalid_credentials' : 'unknown_error';
      trackLoginFailed(reason);
      setSubmitting(false);
      setFormError(message);
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <img src={fiLogo} alt="Fi" />
        </div>

        <div className={`${styles.card} ${shaking ? styles.shake : ''}`}>
          <div className={styles.badge}>Rescue Portal</div>
          <h1 className={styles.title}>Welcome back</h1>
          <p className={styles.subtitle}>Log in to manage your chips, devices, and more.</p>

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className={styles.formField}>
              <label className={styles.label} htmlFor="rescue-email">
                Email
              </label>
              <input
                id="rescue-email"
                className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                type="email"
                placeholder="rescue@example.org"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(false);
                }}
              />
              {emailError && <div className={styles.errorText}>Please enter your email address</div>}
            </div>

            <div className={styles.formField}>
              <label className={styles.label} htmlFor="rescue-password">
                Password
              </label>
              <div className={styles.passwordInputWrapper}>
                <input
                  id="rescue-password"
                  className={`${styles.input} ${styles.passwordInput} ${passwordError ? styles.inputError : ''}`}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.toggleButton}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <img
                    src={showPassword ? eyeHiddenIcon : eyeVisibleIcon}
                    alt={showPassword ? 'Hide password' : 'Show password'}
                    className={styles.toggleIcon}
                  />
                </button>
              </div>
              {passwordError && <div className={styles.errorText}>Please enter your password</div>}
            </div>

            <div className={styles.forgotLink}>
              <Link to="/passwordReset">Forgot password?</Link>
            </div>

            {formError && <div className={styles.formError}>{formError}</div>}

            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? 'Logging in...' : 'Log In'}
            </button>

            <p className={styles.terms}>
              By signing in, you agree to the
              <br />
              <a href="/rescue/terms" target="_blank" rel="noopener noreferrer">
                Fi Rescue Program Terms of Service
              </a>
              .
            </p>
          </form>

          <div className={styles.footer}>
            New rescue? <Link to="/apply">Apply to join</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
